"use client";

/**
 * Help that teaches the calling campaigns without getting in the way: a short
 * "how this works" guide that staff can hide (and bring back), and a one-line
 * explanation that changes, gently, with what's selected. Motion is skipped for
 * anyone who has asked their system for reduced motion (`MotionConfig` in the panels).
 */
import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, HelpCircle, MapPin, X } from "lucide-react";

import { cn } from "@/lib/utils";

export interface GuideStep {
  title: string;
  body: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
}

function readHidden(key: string): boolean {
  try { return window.localStorage.getItem(key) === "hidden"; } catch { return false; }
}
function writeHidden(key: string, hidden: boolean) {
  try { window.localStorage.setItem(key, hidden ? "hidden" : "shown"); } catch { /* private window: just don't remember */ }
}

/** A dismissible three-step guide. Remembered per browser; "How this works" brings it back. */
export function HowItWorks({ storageKey, title, steps }: { storageKey: string; title: string; steps: GuideStep[] }) {
  // These panels only render in the browser (behind sign-in), so storage can
  // be read up front rather than after a first paint that flashes the guide.
  const [hidden, setHidden] = React.useState<boolean>(() => typeof window !== "undefined" && readHidden(storageKey));
  const toggle = (h: boolean) => { setHidden(h); writeHidden(storageKey, h); };

  return (
    <div>
      <AnimatePresence initial={false}>
        {!hidden && (
          <motion.section
            key="guide"
            aria-label={title}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="relative rounded-2xl border border-[#0f6f7c]/20 bg-gradient-to-br from-[#f3f9f9] to-white p-4">
              <button type="button" onClick={() => toggle(true)} aria-label="Hide this guide"
                className="absolute right-2 top-2 inline-flex size-8 items-center justify-center rounded-lg text-slate-400 hover:bg-white hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
                <X className="size-4" />
              </button>
              <h3 className="flex items-center gap-1.5 pr-8 text-sm font-semibold text-[#003d45]">
                <HelpCircle className="size-4 text-[#007a8a]" aria-hidden /> {title}
              </h3>
              <ol className="mt-3 grid gap-3 md:grid-cols-3">
                {steps.map((st, i) => (
                  <motion.li key={st.title}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.25 }}
                    className="flex gap-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#003d45] text-white">
                      <st.icon className="size-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{i + 1}. {st.title}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">{st.body}</span>
                    </span>
                  </motion.li>
                ))}
              </ol>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
      {hidden && (
        <button type="button" onClick={() => toggle(false)}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-[#007a8a] hover:bg-[#e6f0f1] focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
          <HelpCircle className="size-3.5" aria-hidden /> How this works
        </button>
      )}
    </div>
  );
}

/** One line of explanation that cross-fades when its text changes. */
export function HelpLine({ children, className }: { children: string; className?: string }) {
  return (
    <div aria-live="polite" className={cn("relative min-h-5 text-xs text-slate-500", className)}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.p key={children} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.18 }}>
          {children}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

/** A fold-out area (e.g. "More filters") that opens with a height animation. */
export function Reveal({ open, children, id }: { open: boolean; children: React.ReactNode; id?: string }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div id={id} key="reveal"
          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }} className="overflow-hidden">
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** The closest community and centre, for a table cell or a drawer line. */
export function NearestCell({ community, miles, centre }: { community: string | null | undefined; miles: number | null | undefined; centre: string | null | undefined }) {
  if (!community && !centre) return <span className="text-xs text-slate-400">No location on file</span>;
  return (
    <div className="min-w-0 text-[13px] leading-snug">
      {community && (
        <p className="flex items-center gap-1 truncate text-slate-800" title={`Closest active community${miles != null ? `, about ${miles} miles away` : ""}`}>
          <MapPin className="size-3 shrink-0 text-[#007a8a]" aria-hidden />
          <span className="truncate">{community.replace(/\s+Community$/i, "")}</span>
          {miles != null && <span className="shrink-0 text-xs text-slate-400">· {miles < 1 ? "<1" : miles} mi</span>}
        </p>
      )}
      {centre && <p className="truncate pl-4 text-xs text-slate-500" title="Closest centre (geo pipeline)">Centre: {centre}</p>}
    </div>
  );
}

export function Chevron({ open }: { open: boolean }) {
  return <ChevronDown className={cn("size-3.5 transition-transform duration-200", open && "rotate-180")} aria-hidden />;
}
